/**
 * Buying in the phone app — through Apple and Google, never Stripe (the
 * user, 2026-10-07, evening: the stores' rules for digital goods). The page's
 * half; the shell's is `mobile/src/store.ts`, the messages are `@myrave/shared`
 * `nativeBridge.ts`.
 *
 *   page ──iap.buy──▶ shell ──StoreKit / Play Billing──▶ store
 *   page ◀─iap.purchases── shell              (the signed transaction / token)
 *   page ──POST /billing/store/purchases──▶ API ──verifies with Apple / Google
 *   page ──iap.finish──▶ shell                (only once the API has answered)
 *
 * The session is the page's, so the page is what talks to the API; the shell
 * holds nothing. A transaction the API could not answer for (offline, a 5xx)
 * is NOT finished — the store delivers it again, and the quiet restore at the
 * next launch (`syncStorePurchases`) sends it.
 */
import { parseFromNative, type AnyProductId, type EntitlementSummary, type FromNative, type StorePurchase } from '@myrave/shared';
import { api } from './api';
import { inNativeApp } from './nativeBridge';

interface RnWindow { ReactNativeWebView?: { postMessage: (m: string) => void } }

/** In the phone app, the store is how anything is bought — the Stripe form is never shown there. */
export const buysInStore = (): boolean => inNativeApp();

function post(message: unknown): boolean {
  const rn = (window as unknown as RnWindow).ReactNativeWebView;
  if (!rn) return false;
  rn.postMessage(JSON.stringify(message));
  return true;
}

let counter = 0;
const nextId = () => `iap-${Date.now().toString(36)}-${(counter++).toString(36)}`;

function answerFor<T extends FromNative>(requestId: string, timeoutMs: number): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => { window.removeEventListener('myrave:native', on); resolve(null); }, timeoutMs);
    function on(e: Event) {
      const m = parseFromNative((e as CustomEvent).detail);
      if (!m || !('requestId' in m) || m.requestId !== requestId) return;
      window.clearTimeout(timer);
      window.removeEventListener('myrave:native', on);
      resolve(m as T);
    }
    window.addEventListener('myrave:native', on);
  });
}

let account: Promise<{ appAccountToken: string }> | null = null;
/** The UUID every store purchase carries, so the API can tell whose it is. */
function storeAccount() {
  account ??= api.get<{ appAccountToken: string }>('/billing/store/account').catch((e: unknown) => { account = null; throw e; });
  return account;
}

export type StoreOutcome =
  | { status: 'granted'; entitlements: EntitlementSummary }
  | { status: 'pending' }
  | { status: 'cancelled' }
  | { status: 'failed'; message: string };

interface Verified {
  results: { ref: string; status: 'granted' | 'revoked' | 'pending' | 'refused'; product?: string; reason?: string }[];
  entitlements: EntitlementSummary;
}

/** Send store transactions to the API, then finish what it answered for. */
async function verify(platform: 'ios' | 'android', purchases: StorePurchase[], target: { editionId?: string; photoId?: string }): Promise<Verified | null> {
  if (purchases.length === 0) return null;
  const res = await api.post<Verified>('/billing/store/purchases', {
    platform,
    purchases: purchases.map((p) => (platform === 'ios' ? { transaction: p.token } : { productId: p.productId, purchaseToken: p.token })),
    ...target,
  });
  // One result per purchase, in order. A pending one (an Ask to Buy, cash at
  // a shop) stays with the store until it completes.
  const done = purchases.filter((_, i) => res.results[i] && res.results[i]!.status !== 'pending').map((p) => p.id);
  if (done.length) post({ type: 'iap.finish', ids: done });
  return res;
}

/**
 * Buy one product through the store. `editionId` names the rave a single rave
 * is for, `photoId` the photo — the API grants exactly that.
 */
export async function buyInStore(product: AnyProductId, target: { editionId?: string; photoId?: string } = {}): Promise<StoreOutcome> {
  const { appAccountToken } = await storeAccount();
  const requestId = nextId();
  // A store sheet can sit open while someone finds their password.
  const answer = answerFor<Extract<FromNative, { type: 'iap.purchases' | 'iap.failed' }>>(requestId, 15 * 60_000);
  if (!post({ type: 'iap.buy', requestId, product, appAccountToken, ...target })) return { status: 'failed', message: 'not in the app' };
  const got = await answer;
  if (!got) return { status: 'failed', message: 'the store did not answer' };
  if (got.type === 'iap.failed') return got.reason === 'cancelled' ? { status: 'cancelled' } : { status: 'failed', message: got.message ?? '' };
  const verified = await verify(got.platform, got.purchases, target);
  if (!verified) return { status: 'failed', message: 'the store returned no purchase' };
  const statuses = verified.results.map((r) => r.status);
  if (statuses.includes('granted')) return { status: 'granted', entitlements: verified.entitlements };
  if (statuses.includes('pending')) return { status: 'pending' };
  return { status: 'failed', message: verified.results.find((r) => r.reason)?.reason ?? '' };
}

/**
 * Every transaction the store still holds, sent to the API: "Restore
 * purchases" (iOS requires it), or `quiet` on launch for anything paid but
 * never verified. Answers the account's entitlements after.
 */
export async function restoreInStore(quiet = false): Promise<EntitlementSummary | null> {
  const requestId = nextId();
  const answer = answerFor<Extract<FromNative, { type: 'iap.purchases' }>>(requestId, 2 * 60_000);
  if (!post({ type: 'iap.restore', requestId, ...(quiet ? { quiet: true } : {}) })) return null;
  const got = await answer;
  if (!got || got.type !== 'iap.purchases') return null;
  const verified = await verify(got.platform, got.purchases, {});
  return verified?.entitlements ?? api.get<EntitlementSummary>('/me/entitlements');
}

/** The stores' own prices by store product id — the stores require THEIR price on screen. */
export async function storePrices(): Promise<Record<string, string>> {
  const requestId = nextId();
  const answer = answerFor<Extract<FromNative, { type: 'iap.prices' }>>(requestId, 20_000);
  if (!post({ type: 'iap.prices', requestId })) return {};
  return (await answer)?.prices ?? {};
}

/** The store's subscription page, where a plan bought in the store is managed. */
export function manageInStore(): void {
  post({ type: 'iap.manage' });
}

